/* eslint-disable import/no-anonymous-default-export */
import {
  DataProvider,
  fetchUtils,
  RaRecord,
  GetOneParams,
  GetManyParams,
  CreateParams,
  UpdateParams,
  UpdateManyParams,
  DeleteParams,
  DeleteManyParams,
  HttpError,
} from "react-admin";
import simpleRestProvider from "ra-data-simple-rest";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const getToken = () => localStorage.getItem("kc_token");

const BATCH_SIZE = 20;

type CustomDataProvider = DataProvider & {
  getOrdersStats: (resource: string, params: any) => Promise<any>;
  dashbordMetrics: (resource: string, params: any) => Promise<any>;
  getInviteUrl: (resource: string, params: any) => Promise<any>;
};

const chunkArray = <T>(array: T[], size: number): T[][] => {
  if (size <= 0) return [array];

  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const uniqueIds = <T>(ids: T[]): T[] =>
  ids.filter((id, index, array) => {
    const isValid = id !== undefined && id !== null && id !== "";
    return isValid && array.indexOf(id) === index;
  });

const runInBatches = async <T>(
  items: T[],
  handler: (item: T) => Promise<any>,
  batchSize = BATCH_SIZE,
) => {
  const chunks = chunkArray(items, batchSize);
  const results: any[] = [];

  for (const chunk of chunks) {
    const batchResults = await Promise.all(chunk.map(handler));
    results.push(...batchResults);
  }

  return results;
};

const mapMongoRecord = <RecordType extends RaRecord = any>(
  rec: any,
): RecordType =>
  ({
    ...rec,
    id: rec?._id ?? rec?.id,
  }) as RecordType;

// 1️⃣ httpClient catches 400s and rethrows a HttpError with body=fieldErrors
const httpClient = async (url: string, options: any = {}) => {
  options.headers = options.headers || new Headers();

  const token = getToken();
  if (token) {
    options.headers.set("Authorization", `Bearer ${token}`);
  }

  try {
    return await fetchUtils.fetchJson(url, options);
  } catch (error: any) {
    const { status, body } = error;

    if (
      status === 400 &&
      body &&
      typeof body === "object" &&
      body.message &&
      typeof body.message === "object"
    ) {
      throw new HttpError(
        JSON.stringify(body.message) || "Validation error",
        status,
      );
    }

    throw error;
  }
};

// 2️⃣ Base provider uses our httpClient to add Bearer + error handling
const baseProvider = simpleRestProvider(API_URL, httpClient);

// 3️⃣ Export full DataProvider with all overrides (_id → id, search, etc.)
const dataProvider: CustomDataProvider = {
  ...baseProvider,

  // GET LIST → POST /<resource>:search
  getList: async <RecordType extends RaRecord = any>(
    resource: string,
    params: any,
  ): Promise<{ data: RecordType[]; total: number }> => {
    const { page, perPage } = params.pagination;
    const start = (page - 1) * perPage;
    const length = perPage;

    let { field: column, order: dir } = params.sort as any;
    if (column === "id") column = "_id";

    const rawFilters = (params.filter ?? {}) as Record<string, any>;
    const filterTree = rawFilters.filterTree
      ? { ...rawFilters.filterTree }
      : { operator: "and", operands: [] as any[] };

    for (const key of Object.keys(rawFilters)) {
      if (["filterTree", "preFilter", "populate", "projection"].includes(key)) {
        continue;
      }

      let value = rawFilters[key];
      if (value === undefined || value === null || value === "") continue;

      let filterType = "like";

      if (["client", "provider", "outlet"].includes(key)) {
        filterType = "eq";
      } else if (
        [
          "startDate",
          "startDateProvisional",
          "startDateInventory",
          "startDateLastOrderDate",
          "startDateCreatedAt",
          "startDateScheduled",
          "startDateExecution",
        ].includes(key)
      ) {
        filterType = "gte";
      } else if (
        [
          "endDate",
          "endDateProvisional",
          "endDateInventory",
          "endDateLastOrderDate",
          "endDateLastCreatedAt",
          "endDateLastScheduled",
          "endDateLastExecution",
        ].includes(key)
      ) {
        filterType = "lte";
      } else if (
        ["airbagsCount", "fullBinsCount", "emptyBinsCount"].includes(key)
      ) {
        value = value.toString();
        filterType = "gte";
      }

      filterTree.operands.push({
        term: {
          name: key,
          filterType,
          value,
        },
      });
    }

    const projection = (params.filter as any)?.projection;

    let populate: any = [];
    if (params.meta?.populate) {
      populate = params.meta.populate;
    }

    let preFilter: any[] = [];
    if (params.meta?.preFilter) {
      try {
        preFilter = JSON.parse(params.meta.preFilter);
      } catch {
        preFilter = [];
      }
    }

    const body: any = {
      start,
      length,
      order: { column, dir },
      ...(projection && { projection }),
      ...(populate && { populate }),
      preFilter,
      filter: filterTree,
    };

    const url = `${API_URL}/${resource}:search`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const data = ((json.items as any[]) ?? []).map((item) =>
      mapMongoRecord<RecordType>(item),
    );

    const total = json.recordsTotal ?? json.recordsFiltered ?? json.total ?? 0;

    return { data, total };
  },

  // GET ONE → GET /<resource>/:id
  getOne: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams<RecordType>,
  ) => {
    let populate: any = [];
    let populateParams = "";

    if (params.meta?.populate) {
      populate = params.meta.populate;
      populateParams = `?populate=${encodeURIComponent(
        typeof populate === "string" ? populate : JSON.stringify(populate),
      )}`;
    }

    const url = `${API_URL}/${resource}/${params.id}${populateParams}`;
    const { json } = await httpClient(url);

    const rec = json.data ?? json;

    return { data: mapMongoRecord<RecordType>(rec) };
  },

  // GET MANY → GET /<resource>/:id in batches
  getMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams<RecordType>,
  ) => {
    const ids = uniqueIds(params.ids ?? []);

    if (!ids.length) {
      return { data: [] };
    }

    const records = await runInBatches(ids, async (id) => {
      const { json } = await httpClient(`${API_URL}/${resource}/${id}`);
      const rec = json.data ?? json;
      return mapMongoRecord<RecordType>(rec);
    });

    return { data: records };
  },

  // GET MANY REFERENCE → POST /<resource>:search + target filter
  getManyReference: async <RecordType extends RaRecord = any>(
    resource: string,
    params: any,
  ) => {
    const { page, perPage } = params.pagination;
    const start = (page - 1) * perPage;
    const length = perPage;

    let { field: column, order: dir } = params.sort as any;
    if (column === "id") column = "_id";

    const baseTree = (params.filter as any)?.filterTree ?? {
      operator: "and",
      operands: [],
    };

    const refOperand = {
      term: { name: params.target, filterType: "eq", value: params.id },
    };

    const filterTree = {
      operator: "and",
      operands: [...baseTree.operands, refOperand],
    };

    const projection = (params.filter as any)?.projection;
    const populate = (params.filter as any)?.populate;

    const body: any = {
      start,
      length,
      order: { column, dir },
      ...(projection && { projection }),
      ...(populate && { populate }),
      preFilter: [],
      filter: filterTree,
    };

    const url = `${API_URL}/${resource}:search`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const data = ((json.items as any[]) ?? []).map((item) =>
      mapMongoRecord<RecordType>(item),
    );

    const total = json.recordsTotal ?? json.recordsFiltered ?? json.total ?? 0;

    return { data, total };
  },

  // CREATE → POST /<resource>
  create: async <RecordType extends RaRecord = any>(
    resource: string,
    params: CreateParams<RecordType>,
  ) => {
    const url = `${API_URL}/${resource}`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const rec = json.data ?? json;

    return { data: mapMongoRecord<RecordType>(rec) };
  },

  // UPDATE → PUT /<resource>/:id
  update: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams<RecordType>,
  ) => {
    const url = `${API_URL}/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: "PUT",
      body: JSON.stringify(params.data),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const rec = json.data ?? json;

    return { data: mapMongoRecord<RecordType>(rec) };
  },

  // UPDATE MANY → multiple PUT in batches
  updateMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateManyParams<RecordType>,
  ) => {
    const ids = uniqueIds(params.ids ?? []);

    if (!ids.length) {
      return { data: [] };
    }

    await runInBatches(ids, async (id) =>
      httpClient(`${API_URL}/${resource}/${id}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
        headers: new Headers({ "Content-Type": "application/json" }),
      }),
    );

    return { data: ids };
  },

  // DELETE → DELETE /<resource>/:id
  delete: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams<RecordType>,
  ) => {
    const url = `${API_URL}/${resource}/${params.id}`;
    const { json } = await httpClient(url, { method: "DELETE" });

    const rec = json.data ?? json;

    return { data: mapMongoRecord<RecordType>(rec) };
  },

  // DELETE MANY → multiple DELETE in batches
  deleteMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteManyParams<RecordType>,
  ) => {
    const ids = uniqueIds(params.ids ?? []);

    if (!ids.length) {
      return { data: [] };
    }

    await runInBatches(ids, async (id) =>
      httpClient(`${API_URL}/${resource}/${id}`, { method: "DELETE" }),
    );

    return { data: ids };
  },

  // STATS
  getOrdersStats: async (resource: string, params: any) => {
    const query = new URLSearchParams({
      filters: JSON.stringify(params.filters),
    }).toString();

    const url = `${API_URL}/${resource}/stats?${query}`;
    const { json } = await httpClient(url, {
      method: "GET",
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return json.data;
  },

  // Dashboard metrics
  dashbordMetrics: async (resource: string, params: any) => {
    const url = `${API_URL}/${resource}`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(params.filters),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return json.data;
  },

  getInviteUrl: async (resource: string, params: any) => {
    const url = `${API_URL}/${resource}/${params.id}/invite-url`;
    const { json } = await httpClient(url, {
      method: "GET",
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return json.data;
  },
};

export default dataProvider;
